import { computed, inject, Injectable, Injector, Signal, signal, WritableSignal } from '@angular/core';
import {
  AuthResponse,
  CheckEmailPayload,
  CheckEmailResponse,
  ForgotPasswordPayload,
  LogInPayload,
  patchPasswordPayload,
  RefreshTokenResponse,
  ResetPasswordPayload,
  SignUpPayload,
} from '@core/models/entities/auth.models';
import { HttpApi } from './http.api';
import { Router } from '@angular/router';
import { AUTH_RC } from '@core/constants/routes.constants';
import { map, Observable, of, tap } from 'rxjs';
import { environment } from '@environment';
import { Store } from '@ngrx/store';
import { HttpGetOptions } from '@core/models/http.models';
import { RootActions } from '@store/root.actions';
import { SnackbarApiService } from '@core/services/helpers/snackbar-api.service';
import { parseJwt } from '@core/utils/base64.utils';
import { Token } from '@core/models/auth.models';
import { UserRole } from '@core/models/enums/user.enum';
import { Instructor } from '@core/models/entities/instructor.models';
import { User } from '@core/models/entities/user.models';
import { BranchService } from '@core/services/helpers/branch.service';

const ACCESS_TOKEN_KEY: string = 'access_token';
const REFRESH_TOKEN_KEY: string = 'refresh_token';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  readonly userRole = UserRole;

  private readonly injector = inject(Injector);

  private readonly baseUrl: string = environment.apiUrl;

  tokenInfo: WritableSignal<Token | null> = signal(null);

  userType: Signal<string | null> = computed(() => this.tokenInfo()?.role ?? null);

  isInstructor: Signal<boolean> = computed(() => this.tokenInfo()?.role === this.userRole.instructor);

  userByLogin: WritableSignal<User | Instructor | null> = signal(null);

  constructor(
    private httpApi: HttpApi,
    private snackbarApiService: SnackbarApiService,
    private router: Router,
    private store: Store
  ) {
    if (this.isAuthorized() && !this.tokenInfo()) {
      this.getInfoByToken();
    }
  }

  checkEmail(payload: CheckEmailPayload): Observable<CheckEmailResponse> {
    return this.httpApi.post<CheckEmailResponse>(`${this.baseUrl}/auth/user/check-email`, payload);
  }

  logIn(payload: LogInPayload): Observable<AuthResponse> {
    return this.httpApi.post<AuthResponse>(`${this.baseUrl}/auth/user/login`, payload).pipe(
      map((response: AuthResponse) => {
        this.tokenInfo.set(parseJwt(response.accessToken));
        this.setAccessToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
        this.userByLogin.set(response.user);
        this.snackbarApiService.success('Sign In successful');
        return response;
      })
    );
  }

  signUp(payload: SignUpPayload): Observable<AuthResponse> {
    return this.httpApi.post<AuthResponse>(`${this.baseUrl}/auth/user/sign-up`, payload).pipe(
      map((response: AuthResponse) => {
        this.tokenInfo.set(parseJwt(response.accessToken));
        this.setAccessToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
        this.snackbarApiService.success('Sign Up successful');
        return response;
      })
    );
  }

  signUpInstructor(payload: SignUpPayload, token: string): Observable<AuthResponse> {
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    return this.httpApi.patch<AuthResponse>(`${this.baseUrl}/instructor/me/registration`, payload, { headers }).pipe(
      map((res: AuthResponse) => {
        this.snackbarApiService.success('Sign Up successful');
        return res;
      })
    );
  }

  getInfoByToken(): void {
    this.tokenInfo.set(parseJwt(this.getAccessToken()!));
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    const refreshToken: string | null = this.getRefreshToken();

    return this.httpApi
      .post<RefreshTokenResponse>(
        `${this.baseUrl}/auth/new-access-token`,
        { refreshToken },
        { skipSuccessMessage: true }
      )
      .pipe(
        tap((response: RefreshTokenResponse) => {
          this.setAccessToken(response.accessToken);
          this.setRefreshToken(response.refreshToken);
        })
      );
  }

  logOut(url?: string, successCallback?: () => void): Observable<boolean> {
    const redirect = url ? url : `${AUTH_RC.root}/${AUTH_RC.children.logIn}`;
    const onSuccess = () => {
      this.reset();
      this.injector.get(BranchService).clearData();
      this.router.navigate([redirect]);
      this.store.dispatch(RootActions.clearState());
      successCallback?.();
      this.snackbarApiService.success('Log Out successful');
      return true;
    };
    if (!this.isAuthorized()) return of(onSuccess());
    return this.httpApi.post<boolean>(`${this.baseUrl}/auth/logout`, {}, { skipSuccessMessage: true }).pipe(
      tap({
        next: onSuccess,
        error: err => {
          if ([401, 402, 403, 404].includes(err.status)) onSuccess();
        },
      })
    );
  }

  isAuthorized(): boolean {
    return !!this.getAccessToken();
  }

  forgotPassword(payload: ForgotPasswordPayload): Observable<boolean> {
    return this.httpApi.post<boolean>(`${this.baseUrl}/auth/user/forgot-password`, payload).pipe(
      map(res => {
        this.snackbarApiService.success(`The email was sent ${res ? 'successfully' : 'failed'}`);
        return res;
      })
    );
  }

  resetPassword(payload: ResetPasswordPayload, options?: HttpGetOptions): Observable<boolean> {
    return this.httpApi.patch<boolean>(`${this.baseUrl}/auth/reset-password`, payload, options).pipe(
      map(res => {
        this.snackbarApiService.success('The password was successfully changed');
        return res;
      })
    );
  }

  patchPasswordUser(payload: patchPasswordPayload): Observable<boolean> {
    return this.httpApi.patch<boolean>(`${this.baseUrl}/auth/change-password`, payload).pipe(
      map(res => {
        this.snackbarApiService.success('The password was successfully changed');
        return res;
      })
    );
  }

  saveToken(data: { accessToken: string; refreshToken: string }): void {
    this.tokenInfo.set(parseJwt(data.accessToken));
    this.setAccessToken(data.accessToken);
    this.setRefreshToken(data.refreshToken);
  }

  private setAccessToken(token: string | null): void {
    if (token) return localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  private setRefreshToken(token: string | null): void {
    if (token) return localStorage.setItem(REFRESH_TOKEN_KEY, token);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  private reset(): void {
    this.setAccessToken(null);
    this.setRefreshToken(null);
    this.tokenInfo.set(null);
  }
}
